import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Avatar, Box } from "@mui/material";
import { FormikProps } from "formik";
import CustomSelect from "@/components/CustomSelect";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import {
  CHART_COLORS,
  CHART_DIMENSIONS,
  CHART_TYPOGRAPHY,
} from "@/components/app/provider/patient/chart";
import EncounterSign from "@/pages/app/provider/scheduling/appointment/encounter/EncounterSign";
import { ActiveForm } from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note";
import type { Form2Payload } from "@/pages/app/provider/scheduling/appointment/encounter/encounter-constant";
import {
  ATTENDANCE_STATUS_LABEL,
  GROUP_NOTE_TEMPLATE_LABEL,
  INDIVIDUAL_NOTE_LABEL,
  NOTE_TEMPLATE_SELECT_WIDTH,
  SESSION_NOTE_HEADER_SEPARATOR,
} from "../group-visit-constant";
import {
  GroupNoteTemplate,
  SavedEncounterNote,
  SessionParticipant,
} from "../group-visit-types";
import { getGroupNoteTemplateOptions, getInitials } from "../group-visit-utils";

export type IndividualNoteFormProps = {
  participant: SessionParticipant;
  formik: FormikProps<Form2Payload>;
  /** Group type the template list is drawn from. */
  groupType?: string | null;
  template: GroupNoteTemplate;
  onTemplateChange: (value: GroupNoteTemplate) => void;
  /** The saved record the sign-off signs. */
  summary?: SavedEncounterNote;
  onMarkComplete: () => void;
  onPrev: () => void;
  onNext: () => void;
  hasPrev: boolean;
  hasNext: boolean;
  disabled?: boolean;
};

const AVATAR_SIZE = 34;

/**
 * One patient's visit note for the session, on whichever template the group's
 * type supports. It lives in that patient's chart, so group-level content is
 * copied in rather than shared.
 *
 * @author Rajkumar Rathod
 */
const IndividualNoteForm = ({
  participant,
  formik,
  groupType,
  template,
  onTemplateChange,
  summary,
  onMarkComplete,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
  disabled,
}: IndividualNoteFormProps) => {
  const templateOptions = getGroupNoteTemplateOptions(groupType);

  return (
    <Flex flexDirection="column" height="100%" sx={{ minHeight: 0 }}>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 2 }}>
        <Flex flexDirection="column" gap={2}>
          <Flex
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            flexWrap="wrap"
          >
            <Flex alignItems="center" gap={1.5}>
              <Avatar
                sx={{
                  width: AVATAR_SIZE,
                  height: AVATAR_SIZE,
                  fontSize: CHART_DIMENSIONS.iconSm,
                  bgcolor: CHART_COLORS.btnPrimaryBg,
                  color: CHART_COLORS.surfaceWhite,
                }}
              >
                {getInitials(participant.name)}
              </Avatar>

              <Flex flexDirection="column">
                <Text
                  component="h2"
                  {...CHART_TYPOGRAPHY.body3}
                  fontWeight={CHART_TYPOGRAPHY.weight.medium}
                  c={CHART_COLORS.textDarker}
                >
                  {participant.name}
                </Text>
                <Text
                  {...CHART_TYPOGRAPHY.body5}
                  c={CHART_COLORS.textMutedGrey}
                >
                  {[
                    participant.patientId,
                    participant.status &&
                      ATTENDANCE_STATUS_LABEL[participant.status],
                    GROUP_NOTE_TEMPLATE_LABEL[template],
                  ]
                    .filter(Boolean)
                    .join(SESSION_NOTE_HEADER_SEPARATOR)}
                </Text>
              </Flex>
            </Flex>

            <Flex alignItems="center" gap={1}>
              <Box sx={{ width: NOTE_TEMPLATE_SELECT_WIDTH }}>
                <CustomSelect
                  name="formType"
                  options={templateOptions}
                  value={template}
                  disabled={disabled}
                  clearable={false}
                  disableSortOptions
                  onChange={(value) =>
                    value && onTemplateChange(value as GroupNoteTemplate)
                  }
                />
              </Box>
            </Flex>
          </Flex>

          <Box
            sx={{
              px: 1.5,
              py: 1.25,
              borderRadius: CHART_DIMENSIONS.borderRadius,
              backgroundColor: CHART_COLORS.surfaceSubtle,
            }}
          >
            <Text {...CHART_TYPOGRAPHY.body5} c={CHART_COLORS.textMutedGrey}>
              <Text
                component="span"
                {...CHART_TYPOGRAPHY.body5}
                fontWeight={CHART_TYPOGRAPHY.weight.semiBold}
                c={CHART_COLORS.textDark}
              >
                {`${INDIVIDUAL_NOTE_LABEL.privacyPrefix} `}
              </Text>
              {`This note is visible only in ${participant.name}'s chart; patient-specific observations stay private.`}
            </Text>
          </Box>

          {/* The shared note forms carry their own top padding on top of this gap. */}
          <Box sx={{ "& > div > :first-child": { pt: 0 } }}>
            <ActiveForm formik={formik} curForm={template} />
          </Box>
        </Flex>
      </Box>

      <Flex
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        flexWrap="wrap"
        sx={{
          flexShrink: 0,
          px: 2,
          py: 1.5,
          borderTop: `1px solid ${CHART_COLORS.borderLight}`,
        }}
      >
        <Flex alignItems="center" gap={1}>
          <StyledButtonV2
            label={INDIVIDUAL_NOTE_LABEL.prev}
            variant="outlined"
            disabled={!hasPrev}
            onClick={onPrev}
            sIcon={<ArrowBackIcon sx={{ fontSize: CHART_DIMENSIONS.iconSm }} />}
          />
          <StyledButtonV2
            label={INDIVIDUAL_NOTE_LABEL.nextAttendee}
            variant="outlined"
            disabled={!hasNext}
            onClick={onNext}
            eIcon={
              <ArrowForwardIcon sx={{ fontSize: CHART_DIMENSIONS.iconSm }} />
            }
          />
        </Flex>

        <Flex alignItems="center" gap={1}>
          {!disabled && (
            <EncounterSign
              label={INDIVIDUAL_NOTE_LABEL.markComplete}
              prefillSummeryData={summary}
              onSave={onMarkComplete}
            />
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default IndividualNoteForm;
